import { graphql } from "@/config/graphql";
import { useQuery } from "@apollo/client";
import { Box, Typography, alpha, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { StaticDatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";
import { dateWithoutTimezone } from "@/utils/date";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavigationContainer } from "@/components/NavigationButton";

const Header = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
`;
const reportsQuery = graphql(`
  query reportsQuery($params: PlantReportsParams!) {
    plantReports(params: $params) {
      nodes {
        id
        date
        status
      }
    }
    currentUser {
      id
      selectedPlant {
        plant {
          id
        }
      }
    }
  }
`);

export const ReportsHistory = () => {
  const navigate = useNavigate();
  const dateRef = useRef<Date>(new Date());
  const [fetchingMore, setFetchingMore] = useState(false);
  const { data, loading, fetchMore, previousData } = useQuery(reportsQuery, {
    variables: {
      params: {
        status: "SUBMITTED",
      },
    },
    fetchPolicy: "cache-and-network",
  });
  const submittedReports = new Set(
    data?.plantReports.nodes
      .filter((report) => report.status === "SUBMITTED")
      .map((report) => report.date)
  );
  const goHome = () => {
    navigate("/");
  };
  const fetchForMonth = useCallback(
    (month: Date) => {
      setFetchingMore(true);
      dateRef.current = month;
      fetchMore({
        variables: {
          params: {
            status: "SUBMITTED",
            month: format(dateWithoutTimezone(month), "yyyy-MM-dd"),
          },
        },
      }).finally(() => {
        setFetchingMore(false);
      });
    },
    [fetchMore]
  );
  useEffect(() => {
    if (loading) return;
    if (
      data?.currentUser?.selectedPlant?.plant.id !==
      previousData?.currentUser?.selectedPlant?.plant.id
    ) {
      fetchForMonth(dateRef.current);
      console.log(
        "refetch with date:",
        dateRef.current,
        data?.currentUser?.selectedPlant?.plant.id,
        previousData?.currentUser?.selectedPlant?.plant.id
      );
    }
  }, [data, loading, previousData, fetchForMonth]);
  console.log({ data, previousData });
  return (
    <div>
      <Header>
        <Typography variant="h5">Reportes</Typography>
      </Header>
      <StaticDatePicker
        loading={loading || fetchingMore}
        localeText={{
          toolbarTitle: "",
          okButtonLabel: "Ver reporte",
        }}
        disableFuture
        shouldDisableDate={(day: Date) => {
          return !submittedReports.has(
            format(dateWithoutTimezone(day), "yyyy-MM-dd")
          );
        }}
        onMonthChange={fetchForMonth}
        onAccept={(date) => {
          if (!date) return;
          const report = data?.plantReports.nodes.find(
            (report) =>
              report.date === format(dateWithoutTimezone(date), "yyyy-MM-dd")
          );
          navigate(`/historial/${report?.id}`);
        }}
        slotProps={{
          actionBar: {
            actions: ["accept"],
          },
          day: (props) => {
            const currentDate = format(
              dateWithoutTimezone(props.day),
              "yyyy-MM-dd"
            );
            const submittedReport = submittedReports.has(currentDate);

            const color = submittedReport ? "black" : "rgba(0,0,0,0.10)";
            return {
              sx: {
                color,
                transition: "0.5s",
                "&.Mui-selected": {
                  color,
                  fontWeight: "bold",
                  fontSize: "14px",
                  background: (theme) => alpha(theme.palette.primary.main, 0.2),
                  "&:hover, &:focus": {
                    fontWeight: "bold",
                    background: (theme) =>
                      alpha(theme.palette.primary.main, 0.2),
                  },
                },
                "&.Mui-disabled, &.Mui-disabled:not(.Mui-selected)": {
                  textDecoration: "line-through",
                  color,
                },
              },
            };
          },
        }}
      />

      <NavigationContainer handlePrev={goHome} />
    </div>
  );
};
